'use es6';

import { useTheme } from 'foundations-theming/utils';
import FormattedMessage from 'I18n/components/FormattedMessage';
import getLang from 'I18n/utils/getLang';
import unescapedText from 'I18n/utils/unescapedText';
import PortalIdParser from 'PortalIdParser';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Breadcrumbs } from 'foundations-components/transitional/components/Breadcrumbs';
import { ListingPage } from 'foundations-components/transitional/components/ListingPage';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import DocumentHead from 'marketplace-ui-common/components/portals/DocumentHead';
import PrerenderReady from 'marketplace-ui-common/components/prerender/PrerenderReady';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import LoadingWrapper from 'marketplace-ui-common/layout/components/LoadingWrapper';
import MainSection from 'marketplace-ui-common/layout/components/MainSection';
import MainSectionInner from 'marketplace-ui-common/layout/components/MainSectionInner';
import * as ScreenSizes from 'marketplace-ui-common/layout/constants/ScreenSizes';
import { isSucceeded } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import { getIsPrerenderUserAgent } from 'marketplace-ui-common/utils/userAgent';
import ReviewsFilterSortDropdowns from 'marketplace-ui-reviews/components/ReviewsFilterSortDropdowns';
import ReviewsHeader from 'marketplace-ui-reviews/components/ReviewsHeader';
import ReviewsListStats from 'marketplace-ui-reviews/components/ReviewsListStats';
import ReviewsPaginatedList from 'marketplace-ui-reviews/components/ReviewsPaginatedList';
import * as ReviewTypes from 'marketplace-ui-reviews/constants/ReviewType';
import { REVIEWS_PER_PAGE, SEARCH_KEY } from 'marketplace-ui-reviews/constants/ReviewsListQueries';
import { useReviewFilters } from 'marketplace-ui-reviews/hooks/useReviewFilters';
import { useReviewability } from 'marketplace-ui-reviews/hooks/useReviewability';
import { useReviews } from 'marketplace-ui-reviews/hooks/useReviews';
import ReviewFilters from 'marketplace-ui-reviews/records/ReviewFilters';
import { useUsageTracker } from 'marketplace-ui-tracking';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import { useParams } from 'react-router-dom';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import Header from '../../components/detail/Header';
import AllReviewsReviewRenderer from '../../components/detail/reviews/AllReviewsReviewRenderer';
import { AppReviewWizard } from '../../components/detail/reviews/AppReviewWizard';
import ReviewsListEmptyState from '../../components/detail/reviews/ReviewsListEmptyState';
import WriteReviewButton from '../../components/detail/reviews/WriteReviewButton';
import { MINIMUM_REVIEWS_NEEDED_TO_DISPLAY } from '../../constants/reviews';
import { useAppContext } from '../../context/AppContext';
import { PublicOnly, getIsCustomer, getIsPublic, getIsUngatedForUnifiedDetails } from '../../selectors/auth';
import { useListingView } from '../../utils/hooks/useListingView';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const REVIEWS_WIDTH = 900;
const EmptyLoadingComponent = () => /*#__PURE__*/_jsx("div", {});
const AllReviewsPage = () => {
  const trellisTheme = useTheme();
  const {
    slug
  } = useParams();
  const {
    isMobileView
  } = useResponsiveProps({
    isMobileView: {
      [ScreenSizes.XS]: true,
      [ScreenSizes.MD]: false
    }
  });
  const isUngatedForUnifiedDetails = useSelector(getIsUngatedForUnifiedDetails);
  const isCustomer = useSelector(getIsCustomer);
  const isPrerenderUserAgent = getIsPrerenderUserAgent();
  const preferredLanguage = getLang();
  const isPublic = useSelector(getIsPublic);
  const appContext = useAppContext();
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageViewTracked, setPageViewTracked] = useState(false);
  const tracker = useUsageTracker();
  const {
    listingView,
    getListingViewEventProperties
  } = useListingView(isUngatedForUnifiedDetails);
  const {
    name,
    iconUrl,
    id,
    offeringId,
    offering: {
      credentials,
      homemade,
      portalId: offeringPortalId
    },
    tagline
  } = listingView;
  const {
    reviewFilters,
    updateReviewFilters
  } = useReviewFilters(new ReviewFilters({
    reviewTypes: [ReviewTypes.APP],
    entityId: offeringId
  }));
  const {
    unreviewableReason,
    requestState: reviewabilityRequestStatus
  } = useReviewability({
    entityId: offeringId,
    entityPortalId: offeringPortalId,
    isPublicApp: isPublic,
    reviewPending: reviewSubmitted,
    reviewType: 'APP'
  });
  const {
    reviews,
    reviewStats,
    reviewsFetchStatus,
    reviewStatsFetchStatus,
    reviewsTotal,
    reviewsCount
  } = useReviews({
    appContext,
    reviewFilters,
    offeringId,
    reviewType: 'APP'
  });
  const {
    reviews: userReviews
  } = useReviews({
    appContext,
    reviewFilters: new ReviewFilters({
      reviewTypes: [ReviewTypes.APP],
      entityId: isPublic ? undefined : offeringId,
      reviewerPortalId: PortalIdParser.get()
    }),
    offeringId,
    reviewType: 'APP'
  });
  const userReview = userReviews[0];
  const eventProperties = useMemo(() => Object.assign({}, getListingViewEventProperties(), {
    slug,
    screen: 'all reviews',
    preferredLanguage
  }), [getListingViewEventProperties, preferredLanguage, slug]);
  const trackInteraction = useCallback((action, properties = {}) => {
    tracker.track('listing-detail-interaction', Object.assign({
      action
    }, properties, eventProperties));
  }, [tracker, eventProperties]);
  const onUpdateFilters = useCallback(({
    textKey,
    value
  }) => {
    if (textKey === SEARCH_KEY) {
      setSearchTerm(value);
    }
    updateReviewFilters({
      [textKey]: value
    });
    trackInteraction(`change reviews ${textKey}`, {
      section: 'review',
      value
    });
  }, [updateReviewFilters, trackInteraction]);
  const documentTitle = useMemo(() => {
    return unescapedText('appMarketplace.detail.reviews.all.htmlTitle', {
      name
    });
  }, [name]);
  useEffect(() => {
    document.title = documentTitle;
    if (!pageViewTracked) {
      tracker.track('all-reviews-pageview', Object.assign({}, eventProperties));
      setPageViewTracked(true);
    }
  }, [documentTitle, eventProperties, pageViewTracked, tracker]);
  const renderedHeader = useMemo(() => {
    const breadcrumbs = /*#__PURE__*/_jsx(Breadcrumbs, {
      marginBlockEnd: "100",
      marginBlockStart: "0",
      paddingInlineStart: "0",
      children: /*#__PURE__*/_jsx(UIRouterLink, {
        to: `/${slug}#reviews`,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "appMarketplace.detail.reviews.all.back",
          options: {
            name
          }
        })
      })
    });
    return /*#__PURE__*/_jsx(MainSectionInner, {
      children: /*#__PURE__*/_jsx(Header, {
        appId: id,
        appName: name,
        breadcrumbs: breadcrumbs,
        credentials: credentials,
        headingLevel: reviewsCount ? 'h2' : 'h1',
        homemade: homemade,
        iconUrl: iconUrl,
        isCustomer: isCustomer,
        showTabs: false,
        showConnectButton: false,
        showSetUpUrl: false,
        tagline: tagline,
        wrapperStyle: {
          paddingBottom: '24px'
        }
      })
    });
  }, [credentials, name, homemade, iconUrl, id, isCustomer, reviewsCount, tagline, slug]);
  const {
    inline,
    overallDistClassName
  } = useResponsiveProps({
    inline: {
      xs: false,
      md: true
    },
    overallDistClassName: {
      xs: undefined,
      md: 'm-all-auto'
    }
  });
  const renderedReviewsListStats = useMemo(() => {
    if (reviewsCount && offeringId) {
      return /*#__PURE__*/_jsxs("div", {
        style: {
          maxWidth: REVIEWS_WIDTH,
          width: '100%'
        },
        children: [/*#__PURE__*/_jsxs(TFlex, {
          align: "center",
          justify: "between",
          direction: isMobileView ? 'column' : 'row',
          children: [/*#__PURE__*/_jsx(Text, {
            as: "h1",
            marginBlockEnd: "800",
            marginBlockStart: "1000",
            typography: "heading-500",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "appMarketplace.detail.reviews.all.headingText",
              options: {
                name
              }
            })
          }), /*#__PURE__*/_jsx(WriteReviewButton, {
            hasEditableReview: isPublic && !!userReview,
            onClick: () => setWizardOpen(true),
            size: "md",
            unreviewableReason: unreviewableReason,
            marginBlockEnd: !isMobileView ? '400' : undefined
          })]
        }), reviewsCount >= MINIMUM_REVIEWS_NEEDED_TO_DISPLAY && /*#__PURE__*/_jsx(View, {
          marginBlockEnd: "1300",
          children: /*#__PURE__*/_jsx(ReviewsListStats, {
            inline: inline,
            onUpdateFilters: onUpdateFilters,
            overallDistClassName: overallDistClassName,
            reviewStats: reviewStats,
            reviewType: ReviewTypes.APP,
            width: 300
          })
        })]
      });
    } else {
      return /*#__PURE__*/_jsx("div", {});
    }
  }, [isPublic, reviewsCount, offeringId, isMobileView, name, unreviewableReason, inline, onUpdateFilters, overallDistClassName, reviewStats, userReview]);
  const renderedReviewsListBody = useMemo(() => {
    if (reviewsCount) {
      return /*#__PURE__*/_jsxs("div", {
        style: {
          maxWidth: REVIEWS_WIDTH,
          width: '100%'
        },
        children: [/*#__PURE__*/_jsx(ReviewsHeader, {
          onUpdateFilters: onUpdateFilters,
          reviewsCount: reviewsTotal,
          reviewFilters: reviewFilters
        }), /*#__PURE__*/_jsx(ReviewsFilterSortDropdowns, {
          onUpdateFilters: onUpdateFilters,
          reviewFilters: reviewFilters,
          reviewStats: reviewStats
        }), /*#__PURE__*/_jsx(ReviewsPaginatedList, {
          onUpdateFilters: onUpdateFilters,
          maxWidth: REVIEWS_WIDTH,
          ReviewRenderer: AllReviewsReviewRenderer,
          reviews: reviews,
          reviewFilters: reviewFilters,
          reviewsFetchStatus: reviewsFetchStatus,
          reviewsTotal: reviewsTotal,
          searchTerm: searchTerm,
          showPagination: !isPrerenderUserAgent && reviewsTotal >= REVIEWS_PER_PAGE,
          onTrackInteraction: trackInteraction
        })]
      });
    } else {
      return /*#__PURE__*/_jsx(View, {
        marginBlockStart: "1300",
        as: "div",
        children: /*#__PURE__*/_jsx(ReviewsListEmptyState, {
          onWriteReviewClick: () => setWizardOpen(true),
          unreviewableReason: unreviewableReason
        })
      });
    }
  }, [reviewsCount, onUpdateFilters, reviewsTotal, reviewFilters, reviewStats, reviews, reviewsFetchStatus, searchTerm, isPrerenderUserAgent, trackInteraction, unreviewableReason]);
  return /*#__PURE__*/_jsx(LoadingWrapper, {
    requestStatus: reviewabilityRequestStatus,
    children: /*#__PURE__*/_jsxs(NavMarker, {
      name: "DETAIL_REVIEWS_LOAD",
      children: [/*#__PURE__*/_jsx(PublicOnly, {
        children: /*#__PURE__*/_jsx(DocumentHead, {
          children: /*#__PURE__*/_jsx(PrerenderReady, {
            ready: isSucceeded(reviewsFetchStatus)
          })
        })
      }), /*#__PURE__*/_jsxs(ListingPage, {
        contentAreaFlush: true,
        headerComponent: renderedHeader,
        mainSectionBackgroundColor: trellisTheme.color['do-not-use-olaf'],
        pageBackgroundColor: trellisTheme.color['do-not-use-gypsum'],
        children: [/*#__PURE__*/_jsx(MainSection, {
          children: /*#__PURE__*/_jsxs(TFlex, {
            direction: "column",
            align: "center",
            alignSelf: "center",
            paddingBlockEnd: "1300",
            children: [/*#__PURE__*/_jsx(LoadingWrapper, {
              LoadingComponent: EmptyLoadingComponent,
              requestStatus: reviewStatsFetchStatus,
              children: renderedReviewsListStats
            }), renderedReviewsListBody]
          })
        }), wizardOpen && /*#__PURE__*/_jsx(AppReviewWizard, {
          appName: name,
          appIconUrl: iconUrl,
          editedReview: userReview,
          offeringId: offeringId,
          onClose: () => setWizardOpen(false),
          onSuccess: () => {
            setWizardOpen(false);
            setReviewSubmitted(true);
          },
          onTrackInteraction: trackInteraction,
          slug: slug,
          unreviewableReason: unreviewableReason
        })]
      })]
    })
  });
};
export default AllReviewsPage;